import { Box, Button, Checkbox, Modal, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { scheduledSlackMessagesKeys } from "../../../../api/queryKeys";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import TextButton from "../../../../components/buttons/TextButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import { getPrettyDateTime, useLocalTime } from "../../../../utils/timestamp";

export default function RemoveSlackScheduledMessage() {
  const colors = useColors();
  const showLocalTime = useLocalTime();
  const queryClient = useQueryClient();
  const { getScheduledSlackMessages, removeSlackScheduledMessages } = useAPI();
  const [showModal, setShowModal] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState([]);

  const scheduledMessagesQuery = useQuery({
    queryKey: scheduledSlackMessagesKeys.all,
    queryFn: getScheduledSlackMessages,
    select: (response) => response.data,
    enabled: showModal,
  });

  const removeMessagesMutation = useMutation({
    mutationFn: () => removeSlackScheduledMessages(selectedMessages),
    onSuccess: () => {
      Toast.success("Scheduled messages removed successfully");
      setSelectedMessages([]);
      setShowModal(false);
      queryClient.invalidateQueries({
        queryKey: scheduledSlackMessagesKeys.all,
      });
    },
    onError: () => {
      Toast.error("Error removing scheduled messages");
    },
  });

  const isChecked = (scheduledMessage) => {
    return selectedMessages.some(
      (message) =>
        message.id === scheduledMessage.id &&
        message.channel_id === scheduledMessage.channel_id &&
        message.customer_id === scheduledMessage.customer_id
    );
  };

  const handleCheckboxChange = (scheduledMessage) => {
    if (isChecked(scheduledMessage)) {
      setSelectedMessages(
        selectedMessages.filter((message) => message.id !== scheduledMessage.id)
      );
    } else {
      setSelectedMessages([...selectedMessages, scheduledMessage]);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        sx={{
          backgroundColor: colors.secondary_main,
          "&:hover": {
            backgroundColor: colors.secondary_dark,
          },
        }}
        color="primary"
        onClick={() => {
          setShowModal(true);
        }}
      >
        Remove Slack Scheduled Message
      </Button>
      <Modal open={showModal} onClose={() => setShowModal(false)}>
        <Box
          position="absolute"
          borderRadius="10px"
          top="50%"
          left="50%"
          display="flex"
          flexDirection="column"
          gap="16px"
          padding="32px"
          minWidth="600px"
          sx={{
            transform: "translate(-50%, -50%)",
            backgroundColor: colors.modal_bg,
            outline: "none",
            border: `1px solid ${colors.success_48percent}`,
          }}
        >
          <Typography variant="subtitle1">
            Existing Slack Scheduled Messages
          </Typography>
          <Typography variant="body2">
            NOTE - Only messages for customers in the current AWS account are
            shown
          </Typography>
          <Box display="flex" flexDirection="column" gap={1} minHeight="50px">
            {scheduledMessagesQuery.isLoading ? (
              <LoadingIcon size={30} />
            ) : scheduledMessagesQuery.isSuccess &&
              scheduledMessagesQuery.data.length === 0 ? (
              <Typography variant="body2">
                No scheduled messages found
              </Typography>
            ) : scheduledMessagesQuery.isSuccess ? (
              scheduledMessagesQuery.data.map((scheduledMessage) => (
                <Box
                  key={`${scheduledMessage.customer_id}-${scheduledMessage.channel_id}-${scheduledMessage.id}`}
                  display="flex"
                  gap={1}
                  alignItems="center"
                >
                  <Checkbox
                    checked={isChecked(scheduledMessage)}
                    onChange={() => handleCheckboxChange(scheduledMessage)}
                    disabled={removeMessagesMutation.isPending}
                  />
                  <Typography variant="caption" noWrap>
                    <b>{scheduledMessage.customer_id}</b>
                    <br />
                    Scheduled to:{" "}
                    {getPrettyDateTime(scheduledMessage.post_at, showLocalTime)}
                    <br />
                    Channel: {scheduledMessage.channel_id}
                    <br />
                    Message ID: {scheduledMessage.id}
                    <br />
                    {scheduledMessage.text}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2">Error loading content</Typography>
            )}
          </Box>
          <Box display="flex" justifyContent="center" gap="16px">
            <TextButton onClick={() => setShowModal(false)}>Cancel</TextButton>
            <ContainedButton
              onClick={() => removeMessagesMutation.mutate()}
              color="primary"
              size="medium"
              disabled={
                selectedMessages.length === 0 ||
                scheduledMessagesQuery.isPending
              }
              loading={removeMessagesMutation.isPending}
            >
              Remove
            </ContainedButton>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
